import type { components } from '@/shared/api/schema'
import { numericIssue } from '@/shared/numeric'

export type Invoice = components['schemas']['SupplierInvoiceOutput']
export type InvoiceLine = components['schemas']['InvoiceLineOutput']
export type InvoiceSource = components['schemas']['InvoiceSourceOutput']
export type LineDraft = { product_id: number; unit_id: number; label: string; unit: string;
  unit_price: string; tax_rate: string; sources: { receipt_line_id: number; label: string; quantity: string }[] }
export const invoiceReadPermissions = ['finance.view_supplierinvoice', 'finance.view_payable', 'procurement.view_purchase_amount']

/** Integer-scaled calculation only. Financial totals remain server-authoritative. */
export function sumQuantities(values: string[]): string {
  const amount = values.reduce((sum, value) => {
    if (!value || numericIssue(value, { key: 'quantity', kind: 'decimal',
      numeric: { precision: 3, maxDigits: 18, min: '0.001' } })) throw new Error('quantity')
    const [whole = '0', fraction = ''] = value.split('.')
    return sum + BigInt((whole || '0') + fraction.padEnd(3, '0'))
  }, 0n)
  if (amount > 999999999999999999n) throw new Error('quantity')
  return `${amount / 1000n}.${String(amount % 1000n).padStart(3, '0')}`
}

export function invoiceLinePayload(line: LineDraft): components['schemas']['InvoiceLineInput'] {
  if (!line.sources.length) throw new Error('source')
  if (new Set(line.sources.map(row => row.receipt_line_id)).size !== line.sources.length) throw new Error('source')
  const quantity = sumQuantities(line.sources.map(row => row.quantity))
  if (line.unit.toUpperCase() !== 'KG' && line.sources.some(row => !/^\d+(?:\.0{1,3})?$/.test(row.quantity))) {
    throw new Error('integer')
  }
  for (const key of ['unit_price', 'tax_rate'] as const) {
    if (!line[key] || numericIssue(line[key], { key, kind: 'decimal', numeric: {
      precision: 4, maxDigits: key === 'tax_rate' ? 7 : 18, min: '0', ...(key === 'tax_rate' ? { max: '100' } : {}),
    } })) throw new Error(key)
  }
  return { product_id: line.product_id, unit_id: line.unit_id, quantity, unit_price: line.unit_price,
    tax_rate: line.tax_rate, sources: line.sources.map(({ receipt_line_id, quantity }) => ({ receipt_line_id, quantity })) }
}

export function correctionDraft(line: InvoiceLine, locale = 'zh-CN'): LineDraft {
  return { product_id: line.product, unit_id: line.unit, label: `${line.product_sku} · ${locale === 'en' ? line.product_name_en || line.product_name : line.product_name}`,
    unit: line.unit_code, unit_price: line.unit_price, tax_rate: line.tax_rate,
    sources: line.matches.filter(row => row.status !== 'released').map(row => ({ receipt_line_id: row.receipt_line,
      label: `${row.receipt_no} / ${row.payable_no}`, quantity: row.matched_quantity })) }
}
